import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import logger from '../utils/simpleLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error:', error, JSON.stringify(errorInfo));
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 animate-fade-in">
          <div className="max-w-md w-full text-center space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-nb-error/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7 text-nb-error" />
            </div>

            <div>
              <h2 className="text-lg font-medium text-nb-text mb-1">Algo salio mal</h2>
              <p className="text-sm text-nb-text-secondary">
                Ocurrio un error inesperado. Puedes intentar recargar esta seccion.
              </p>
            </div>

            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-nb-accent text-white hover:bg-nb-accent-hover active:scale-[0.98] transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>

            <details className="text-left mt-4">
              <summary className="cursor-pointer text-xs text-nb-text-muted hover:text-nb-text-secondary transition-colors">
                Detalles del error
              </summary>
              <pre className="mt-2 text-xs overflow-auto bg-nb-surface border border-nb-border p-3 rounded-lg text-nb-text-secondary max-h-40">
                {this.state.error?.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
